import { customerLibraryEnabled } from '@/constants/features';
import Auth from '@/wrappers/auth';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

const MainLayout = lazy(() => import('@/layouts'));
const NextLayout = lazy(() => import('@/layouts/next'));
const Login = lazy(() => import('@/pages/login'));
const LoginNext = lazy(() => import('@/pages/login-next'));
const ChatShare = lazy(() => import('@/pages/chat/share'));
const Knowledge = lazy(() => import('@/pages/knowledge'));
const AddKnowledge = lazy(() => import('@/pages/add-knowledge'));
const KnowledgeDataset = lazy(
  () => import('@/pages/add-knowledge/components/knowledge-dataset'),
);
const KnowledgeFile = lazy(
  () => import('@/pages/add-knowledge/components/knowledge-file'),
);
const KnowledgeChunk = lazy(
  () => import('@/pages/add-knowledge/components/knowledge-chunk'),
);
const KnowledgeSetting = lazy(
  () => import('@/pages/add-knowledge/components/knowledge-setting'),
);
const KnowledgeTesting = lazy(
  () => import('@/pages/add-knowledge/components/knowledge-testing'),
);
const KnowledgeGraph = lazy(
  () => import('@/pages/add-knowledge/components/knowledge-graph'),
);
const KnowledgeImages = lazy(
  () => import('@/pages/add-knowledge/components/knowledge-images'),
);
const Chat = lazy(() => import('@/pages/chat'));
const UserSetting = lazy(() => import('@/pages/user-setting'));
const UserProfile = lazy(() => import('@/pages/user-setting/setting-profile'));
const UserLocale = lazy(() => import('@/pages/user-setting/setting-locale'));
const UserPassword = lazy(
  () => import('@/pages/user-setting/setting-password'),
);
const UserModel = lazy(() => import('@/pages/user-setting/setting-model'));
const UserTeam = lazy(() => import('@/pages/user-setting/setting-team'));
const UserSystem = lazy(() => import('@/pages/user-setting/setting-system'));
const UserApi = lazy(() => import('@/pages/user-setting/setting-api'));
const FileManager = lazy(() => import('@/pages/file-manager'));
const Write = lazy(() => import('@/pages/write'));
const Search = lazy(() => import('@/pages/search'));
const DocumentViewer = lazy(() => import('@/pages/document-viewer'));
const Home = lazy(() => import('@/pages/home'));
const Datasets = lazy(() => import('@/pages/datasets'));
const NextChats = lazy(() => import('@/pages/next-chats'));
const NextChat = lazy(() => import('@/pages/next-chats/chat'));
const Customers = lazy(() => import('@/pages/customers'));
const MobileCustomers = lazy(() => import('@/pages/mobile-customers'));
const NextSearches = lazy(() => import('@/pages/next-searches'));
const NextSearch = lazy(() => import('@/pages/next-search'));
const Files = lazy(() => import('@/pages/files'));
const DatasetWrapper = lazy(() => import('@/pages/dataset'));
const Dataset = lazy(() => import('@/pages/dataset/dataset'));
const DatasetSetting = lazy(() => import('@/pages/dataset/setting'));
const DatasetTesting = lazy(() => import('@/pages/dataset/testing'));
const Chunk = lazy(() => import('@/pages/chunk'));
const ParsedResult = lazy(() => import('@/pages/chunk/parsed-result'));
const ChunkResult = lazy(() => import('@/pages/chunk/chunk-result'));
const ResultView = lazy(() => import('@/pages/chunk/result-view'));
const ProfileSetting = lazy(() => import('@/pages/profile-setting'));
const Profile = lazy(() => import('@/pages/profile-setting/profile'));
const Plan = lazy(() => import('@/pages/profile-setting/plan'));
const Model = lazy(() => import('@/pages/profile-setting/model'));
const Prompt = lazy(() => import('@/pages/profile-setting/prompt'));
const NotFound = lazy(() => import('@/pages/404'));

function Page({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

export function AppRoutes() {
  if (process.env.NATIVE_API_URL) {
    return (
      <Page>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/m/customers" element={<MobileCustomers />} />
          <Route
            path="/m/customers/:customerId"
            element={<MobileCustomers />}
          />
          <Route
            path="/m/customers/:customerId/visits/:visitId"
            element={<MobileCustomers />}
          />
          <Route
            path="/m/customers/:customerId/visits/:visitId/processing"
            element={<MobileCustomers />}
          />
          <Route path="*" element={<Navigate to="/m/customers" replace />} />
        </Routes>
      </Page>
    );
  }

  return (
    <Page>
      <Routes>
        {customerLibraryEnabled && (
          <Route path="/m/customers" element={<MobileCustomers />}>
            <Route path=":customerId" element={<MobileCustomers />} />
            <Route
              path=":customerId/visits/:visitId/processing"
              element={<MobileCustomers />}
            />
            <Route
              path=":customerId/visits/:visitId"
              element={<MobileCustomers />}
            />
          </Route>
        )}
        <Route path="/login" element={<Login />} />
        <Route path="/login-next" element={<LoginNext />} />
        <Route path="/chat/share" element={<ChatShare />} />
        <Route
          path="/document/:id"
          element={
            <Auth>
              <DocumentViewer />
            </Auth>
          }
        />

        <Route
          path="/"
          element={
            <Auth>
              <MainLayout />
            </Auth>
          }
        >
          <Route index element={<Navigate to="/login" replace />} />
          <Route path="knowledge" element={<Knowledge />} />
          <Route path="knowledge" element={<AddKnowledge />}>
            <Route path="dataset" element={<KnowledgeDataset />}>
              <Route index element={<KnowledgeFile />} />
              <Route path="chunk" element={<KnowledgeChunk />} />
            </Route>
            <Route path="configuration" element={<KnowledgeSetting />} />
            <Route path="testing" element={<KnowledgeTesting />} />
            <Route path="knowledgeGraph" element={<KnowledgeGraph />} />
            <Route path="images" element={<KnowledgeImages />} />
          </Route>
          <Route path="chat" element={<Chat />} />
          {customerLibraryEnabled ? (
            <Route path="customers">
              <Route index element={<Customers />} />
              <Route path="new" element={<Customers />} />
              <Route path=":customerId" element={<Customers />} />
              <Route path=":customerId/edit" element={<Customers />} />
            </Route>
          ) : (
            <Route
              path="customers/*"
              element={<Navigate to="/knowledge" replace />}
            />
          )}
          <Route path="user-setting" element={<UserSetting />}>
            <Route index element={<Navigate to="profile" replace />} />
            <Route path="profile" element={<UserProfile />} />
            <Route path="locale" element={<UserLocale />} />
            <Route path="password" element={<UserPassword />} />
            <Route path="model" element={<UserModel />} />
            <Route path="team" element={<UserTeam />} />
            <Route path="system" element={<UserSystem />} />
            <Route path="api" element={<UserApi />} />
          </Route>
          <Route path="file" element={<FileManager />} />
          <Route path="write" element={<Write />} />
          <Route path="search" element={<Search />} />
        </Route>

        <Route
          path="/home"
          element={
            <Auth>
              <NextLayout />
            </Auth>
          }
        >
          <Route index element={<Home />} />
        </Route>
        <Route
          path="/datasets"
          element={
            <Auth>
              <NextLayout />
            </Auth>
          }
        >
          <Route index element={<Datasets />} />
        </Route>
        <Route
          path="/next-chats"
          element={
            <Auth>
              <NextLayout />
            </Auth>
          }
        >
          <Route index element={<NextChats />} />
        </Route>
        <Route
          path="/next-chat"
          element={
            <Auth>
              <NextChat />
            </Auth>
          }
        />
        <Route
          path="/next-searches"
          element={
            <Auth>
              <NextLayout />
            </Auth>
          }
        >
          <Route index element={<NextSearches />} />
        </Route>
        <Route
          path="/next-search"
          element={
            <Auth>
              <NextSearch />
            </Auth>
          }
        />
        <Route
          path="/files"
          element={
            <Auth>
              <NextLayout />
            </Auth>
          }
        >
          <Route index element={<Files />} />
        </Route>
        <Route
          path="/dataset"
          element={
            <Auth>
              <NextLayout />
            </Auth>
          }
        >
          <Route index element={<Navigate to="/dataset/dataset" replace />} />
        </Route>
        <Route
          path="/dataset"
          element={
            <Auth>
              <DatasetWrapper />
            </Auth>
          }
        >
          <Route path="dataset/:id" element={<Dataset />} />
          <Route path="setting/:id" element={<DatasetSetting />} />
          <Route path="testing/:id" element={<DatasetTesting />} />
        </Route>
        <Route
          path="/chunk"
          element={
            <Auth>
              <Chunk />
            </Auth>
          }
        >
          <Route path="parsed/:id" element={<ParsedResult />} />
          <Route path="chunk/:id" element={<ChunkResult />} />
          <Route path="result/:id" element={<ResultView />} />
        </Route>
        <Route
          path="/profile-setting"
          element={
            <Auth>
              <ProfileSetting />
            </Auth>
          }
        >
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<Profile />} />
          <Route path="team" element={<Navigate to="../profile" replace />} />
          <Route path="plan" element={<Plan />} />
          <Route path="model" element={<Model />} />
          <Route path="prompt" element={<Prompt />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Page>
  );
}
